import CustomLoadingButton from 'Components/CustomLoadingButton'
import FormTextField from 'Components/FormTextField'
import InfoTooltip from 'Components/InfoTooltip'
import StyledForm from 'Components/StyledForm'
import { SELECTED_PRODUCT_PARAMETER_KEY } from 'Services/ProductManagement/Features/Product/Hooks/useProductsWithParams'
import {
  useCreateSingleProductMutation,
  useUpdateSingleProductMutation
} from 'Services/ProductManagement/Features/Product/productApi'
import { type Product, ProductUsages } from 'Services/ProductManagement/Features/Product/types'
import type { Translation } from 'Store'
import type { ProductTariffError } from 'Util/ApiClient'
import { FORM_FIELD_DEFAULT_MIN_WIDTH } from 'Util/constants'
import { useValidationErrors } from 'Util/hooks/useValidationErrors'
import SnackbarUtils from 'Util/Snackbar'
import { appendQueryParamsToUrl } from 'Util/url'
import {
  Box,
  FormControl,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Typography
} from '@mui/material'
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { t } from 'ttag'

const fieldErrorsDefaultState: Record<string, string[]> = {
  shared_usage: [],
  max_trips_per_day: [],
  max_device_transfers: [],
  max_days_per_product: [],
  max_trips_per_product: []
}

interface PartialTariffProductFormProps {
  product?: Product
  successBaseUrl: string
}

export default function PartialTariffProductForm({
  product,
  successBaseUrl
}: Readonly<PartialTariffProductFormProps>) {
  const navigate = useNavigate()
  const isEdit = !!product

  const [createSingleProduct, createStatus] = useCreateSingleProductMutation()
  const [updateSingleProduct, updateStatus] = useUpdateSingleProductMutation()

  const mutationStatus = isEdit ? updateStatus : createStatus
  const { isLoading: isSubmitLoading } = mutationStatus

  const [productDetails, setProductDetails] = useState<Partial<Product>>(
    (isEdit && product) || {
      activation_window: { duration: null, period: [] }
    }
  )
  const { fieldErrors, setValidationErrors, getFieldErrorHelperText, getErrorProps } =
    useValidationErrors(fieldErrorsDefaultState)

  // biome-ignore lint/correctness/useExhaustiveDependencies: suppress handleProductISODurations
  useEffect(() => {
    if (isEdit && product) {
      setProductDetails(product)
    }
  }, [product])

  const handleChange = (
    value: string | string[] | Translation[] | number | boolean | undefined | null,
    field: keyof Product
  ) => {
    if (value !== undefined) {
      setProductDetails(prevState => ({ ...prevState, [field]: value }) as Product)
    }
  }

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    const productData = {
      ...productDetails
    }

    const mutation = isEdit
      ? updateSingleProduct({ ...productData, id: product.id })
      : createSingleProduct(productData)

    mutation
      .unwrap()
      .then((productResponse: Product) => {
        SnackbarUtils.success(t`Product successfully saved.`)
        navigate(
          appendQueryParamsToUrl(successBaseUrl, {
            [SELECTED_PRODUCT_PARAMETER_KEY]: productResponse.id
          })
        )
      })
      .catch(({ data: errorResponse }: { data: ProductTariffError }) => {
        const { violations } = errorResponse
        setValidationErrors(violations)
        SnackbarUtils.error(t`There was an error saving the product.`)
      })
  }

  const sharedUsageOptions = [
    {
      label: t`Single user`,
      value: ProductUsages.SINGLE_USER
    },
    {
      label: t`Concurrent users`,
      value: ProductUsages.CONCURRENT_USERS
    }
  ]

  return (
    <Grid container spacing={2} mb={4}>
      <Grid size={12}>
        <StyledForm onSubmit={handleSubmit} isDisabled={isSubmitLoading}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <FormControl sx={{ minWidth: FORM_FIELD_DEFAULT_MIN_WIDTH }} required>
              <InputLabel size="small">{t`Shared usage`}</InputLabel>
              <Select
                name="shared_usage"
                required
                size="small"
                label={t`Shared usage`}
                value={productDetails?.shared_usage ?? ''}
                error={fieldErrors.shared_usage?.length > 0}
                onChange={e => handleChange(e.target.value, 'shared_usage')}
              >
                {sharedUsageOptions.map(({ label, value }) => (
                  <MenuItem key={value} value={value}>
                    {label}
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText error={fieldErrors.shared_usage?.length > 0}>
                {getFieldErrorHelperText('shared_usage')}
              </FormHelperText>
            </FormControl>
            <Box sx={{ p: 1 }}>
              <InfoTooltip
                title={
                  <Typography variant="body2">
                    {t`The value determines whether the product can be used only by the travel account holder or also by fellow passengers.`}
                  </Typography>
                }
              />
            </Box>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <FormTextField
              name="max_trips_day"
              label={t`Maximum trips per day`}
              type="number"
              value={productDetails?.max_trips_per_day ?? ''}
              onChange={e =>
                handleChange(
                  e.target.value === '' ? null : Number.parseInt(e.target.value, 10),
                  'max_trips_per_day'
                )
              }
              {...getErrorProps('max_trips_per_day')}
            />
            <Box sx={{ p: 1 }}>
              <InfoTooltip
                title={
                  <Typography variant="body2">
                    {t`How many daily trips are included in the ticket. A transfer within the transfer time limit does not count as a trip.`}
                  </Typography>
                }
              />
            </Box>
          </Box>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <FormTextField
                name="max_device_transfers"
                label={t`Maximum device transfers`}
                type="number"
                value={productDetails?.max_device_transfers ?? ''}
                onChange={e =>
                  handleChange(
                    e.target.value === '' ? null : Number.parseInt(e.target.value, 10),
                    'max_device_transfers'
                  )
                }
                {...getErrorProps('max_device_transfers')}
              />
              <Box sx={{ p: 1 }}>
                <InfoTooltip
                  title={
                    <Typography variant="body2">
                      {t`How many times the ticket can be transferred from one phone to another.`}
                    </Typography>
                  }
                />
              </Box>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <FormTextField
                name="max_days_per_product"
                label={t`Maximum days per product`}
                type="number"
                value={productDetails?.max_days_per_product ?? ''}
                onChange={e =>
                  handleChange(
                    e.target.value === '' ? null : Number.parseInt(e.target.value, 10),
                    'max_days_per_product'
                  )
                }
                {...getErrorProps('max_days_per_product')}
              />
              <Box sx={{ p: 1 }}>
                <InfoTooltip
                  title={
                    <Typography variant="body2">
                      {t`How many individual travel days are included in the ticket.`}
                    </Typography>
                  }
                />
              </Box>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <FormTextField
                name="max_trips_per_product"
                label={t`Maximum trips per product`}
                type="number"
                value={productDetails?.max_trips_per_product ?? ''}
                onChange={e =>
                  handleChange(
                    e.target.value === '' ? null : Number.parseInt(e.target.value, 10),
                    'max_trips_per_product'
                  )
                }
                {...getErrorProps('max_trips_per_product')}
              />
              <Box sx={{ p: 1 }}>
                <InfoTooltip
                  title={
                    <Typography variant="body2">
                      {t`How many individual trips are included in the ticket.`}
                    </Typography>
                  }
                />
              </Box>
            </Box>
          </Box>
          <CustomLoadingButton
            isLoading={isSubmitLoading}
            type="submit"
            size="large"
            iconPosition="end"
          >
            {t`Save product`}
          </CustomLoadingButton>
        </StyledForm>
      </Grid>
    </Grid>
  )
}
